import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  PenTool,
  Image as ImageIcon,
  Type,
  ShieldCheck,
  Download,
  Clock,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Send,
  Info,
  RotateCcw,
  ArrowLeft,
  Home,
  KeyRound,
  Smartphone,
} from 'lucide-react';
import { signerApi, signatureApi } from '../services/api';
import { formatDateTime } from '@pdfsign/shared/utils';
import { SignatureType } from '@pdfsign/shared/types';
import { toast, confirmDialog } from '../stores/feedbackStore';
import { useAuthStore } from '../stores/authStore';
import { useSmartCaStore } from '../stores/smartCaStore';
import SmartCaModal from '../components/SmartCaModal';
import SignaturePad from '../components/SignaturePad';
import InteractivePdfViewer, { SignaturePlacement } from '../components/InteractivePdfViewer';

interface SignerInfo {
  id: string;
  document_id: string;
  email: string;
  full_name: string;
  role: string;
  order_index: number;
  status: string;
  signature_type: string | null;
  signature_data: any;
  signed_at: string | null;
  decline_reason: string | null;
  invited_at: string;
}

interface DocumentInfo {
  id: string;
  title: string;
  status: string;
  workflow_type: string;
  file_name: string;
  pages_count: number;
  settings: any;
  expires_at: string | null;
}

interface Position {
  id: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  label: string | null;
}

const signatureTypes: Array<{ value: SignatureType; label: string; description: string; icon: any }> = [
  { value: 'draw', label: 'Vẽ chữ ký', description: 'Dùng chuột hoặc cảm ứng để vẽ', icon: PenTool },
  { value: 'image', label: 'Tải ảnh chữ ký', description: 'Tải lên ảnh chữ ký đã có sẵn', icon: ImageIcon },
  { value: 'text', label: 'Gõ tên', description: 'Tạo chữ ký từ tên của bạn', icon: Type },
  { value: 'digital', label: 'Chữ ký số', description: 'Ký bằng chứng thư số (PKI)', icon: ShieldCheck },
];

export default function SigningPage() {
  const { signerId } = useParams<{ signerId: string }>();
  const navigate = useNavigate();

  const [signer, setSigner] = useState<SignerInfo | null>(null);
  const [document, setDocument] = useState<DocumentInfo | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [signatureType, setSignatureType] = useState<SignatureType>('draw');
  const [signatureData, setSignatureData] = useState<any>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [stampPage, setStampPage] = useState(1);
  const [selectedPlacement, setSelectedPlacement] = useState<SignaturePlacement | null>(null);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [textSignature, setTextSignature] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // SmartCA state
  const { config: smartCaConfig } = useSmartCaStore();
  const [showSmartCaModal, setShowSmartCaModal] = useState(false);
  const [smartCaPrompting, setSmartCaPrompting] = useState(false);

  const loadSigner = useCallback(async () => {
    if (!signerId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await signerApi.get(signerId);
      const data = response.data.data;
      setSigner(data.signer);
      setDocument(data.document);
      const posList = data.positions || [];
      setPositions(posList);
      if (posList.length > 0 && posList[0].page) {
        setCurrentPage(posList[0].page);
        setStampPage(posList[0].page);
        setSelectedPlacement({
          page: posList[0].page,
          x: posList[0].x,
          y: posList[0].y,
          width: posList[0].width,
          height: posList[0].height,
        });
      } else {
        // Multi-page administrative reports/proposals: main signature block is on page 2 (or 1 for 1-page doc)
        const defPage = (data.document?.pages_count && data.document.pages_count > 1) ? 2 : 1;
        setCurrentPage(defPage);
        setStampPage(defPage);
      }

      // Mark as viewed
      if (['invited', 'pending'].includes(data.signer.status)) {
        signerApi.view(signerId).catch(() => {});
      }

      // Prefill saved signature
      if (data.signer.signature_data) {
        setSignatureData(data.signer.signature_data);
        setHasSignature(true);
        if (data.signer.signature_type) {
          setSignatureType(data.signer.signature_type as SignatureType);
        }
        if (data.signer.signature_data.text) {
          setTextSignature(data.signer.signature_data.text);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tải thông tin ký');
    } finally {
      setLoading(false);
    }
  }, [signerId]);

  useEffect(() => {
    loadSigner();
  }, [loadSigner]);

  const handleDrawChange = async (payload: { strokes: any[]; dataUrl: string } | null) => {
    if (!payload) {
      setSignatureData(null);
      setHasSignature(false);
      return;
    }
    const data = { strokes: payload.strokes, dataUrl: payload.dataUrl };
    setSignatureData(data);
    setHasSignature(true);

    // Persist draft to server
    if (signerId) {
      try {
        await signatureApi.saveDraw({ signer_id: signerId, strokes: payload.strokes });
      } catch {
        // non-blocking
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !signer || !document) return;

    if (!file.type.startsWith('image/')) {
      setError('Vui lòng chọn file ảnh (PNG, JPG)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Ảnh không được vượt quá 5MB');
      return;
    }

    try {
      // Convert white/light paper background to transparent PNG so it never covers underlying document text
      const rawDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const processed = await new Promise<{ dataUrl: string; blob: Blob; w: number; h: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = window.document.createElement('canvas');
          canvas.width = img.naturalWidth || 400;
          canvas.height = img.naturalHeight || 200;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve({ dataUrl: rawDataUrl, blob: file, w: canvas.width, h: canvas.height });
            return;
          }
          ctx.drawImage(img, 0, 0);
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const d = imgData.data;
          // Filter out white and light gray background pixels (paper background)
          for (let i = 0; i < d.length; i += 4) {
            const r = d[i];
            const g = d[i + 1];
            const b = d[i + 2];
            if (r > 190 && g > 190 && b > 190) {
              d[i + 3] = 0; // Transparent
            }
          }
          ctx.putImageData(imgData, 0, 0);
          const transparentDataUrl = canvas.toDataURL('image/png');
          canvas.toBlob((blob) => {
            resolve({
              dataUrl: transparentDataUrl,
              blob: blob || file,
              w: canvas.width,
              h: canvas.height,
            });
          }, 'image/png');
        };
        img.onerror = reject;
        img.src = rawDataUrl;
      });

      setImagePreview(processed.dataUrl);

      // Upload to R2 as transparent PNG
      const pngFilename = `${file.name.replace(/\.[^.]+$/, '')}.png`;
      const urlRes = await signatureApi.getUploadUrl({
        signer_id: signer.id,
        document_id: signer.document_id,
        filename: pngFilename,
        content_type: 'image/png',
      });
      const { r2_key, uploadUrl } = urlRes.data.data;

      const buffer = await processed.blob.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const fileHash = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');

      const res = await fetch(
        uploadUrl.startsWith('http')
          ? uploadUrl
          : `${(import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '')}${uploadUrl.replace(/^\/api/, '')}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'image/png' },
          body: processed.blob,
        }
      );
      if (!res.ok) throw new Error('Upload failed');

      await signatureApi.confirmUpload({
        signer_id: signer.id,
        r2_key,
        file_hash: fileHash,
        image_width: processed.w,
        image_height: processed.h,
      });

      setSignatureData({
        image_r2_key: r2_key,
        image_width: processed.w,
        image_height: processed.h,
        dataUrl: processed.dataUrl,
      });
      setHasSignature(true);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tải ảnh chữ ký');
    }
  };

  const handleTextChange = async (value: string) => {
    setTextSignature(value);
    if (!value.trim()) {
      setHasSignature(false);
      setSignatureData(null);
      return;
    }
    setHasSignature(true);
    setSignatureData({ text: value, fontFamily: 'cursive', fontSize: 28, color: '#1e3a8a' });

    if (signerId) {
      try {
        await signatureApi.saveText({
          signer_id: signerId,
          text: value,
          font_family: 'cursive',
          font_size: 28,
          color: '#1e3a8a',
        });
      } catch {
        // non-blocking
      }
    }
  };

  /**
   * Rasterize a typed name so Vietnamese diacritics render correctly in the
   * final PDF (pdf-lib's StandardFonts are Latin-1 only).
   */
  const renderTextSignature = (text: string): string => {
    const canvas = window.document.createElement('canvas');
    const dpr = 3;
    canvas.width = 600 * dpr;
    canvas.height = 200 * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, 600, 200);
    ctx.fillStyle = '#1e3a8a';
    ctx.font = 'italic 64px "Segoe Script", "Brush Script MT", cursive';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 300, 100, 560);
    return canvas.toDataURL('image/png');
  };

  const renderDigitalSignature = (signerName: string, caIssuer: string): string => {
    const canvas = window.document.createElement('canvas');
    const dpr = 3;
    canvas.width = 300 * dpr;
    canvas.height = 90 * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, 300, 90);

    // Clean blue border
    ctx.strokeStyle = '#0284c7';
    ctx.lineWidth = 2;
    ctx.strokeRect(3, 3, 294, 84);

    ctx.fillStyle = '#0369a1';
    ctx.font = 'bold 11px sans-serif';
    ctx.fillText('ĐÃ KÝ SỐ ĐIỆN TỬ VNPT SmartCA', 12, 22);

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 13px sans-serif';
    ctx.fillText(signerName.toUpperCase(), 12, 42);

    ctx.fillStyle = '#64748b';
    ctx.font = '10px sans-serif';
    ctx.fillText(`Cung cấp bởi: ${caIssuer}`, 12, 60);

    const now = new Date();
    const dateStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')} ${now.toLocaleDateString('vi-VN')}`;
    ctx.fillText(`Thời gian ký: ${dateStr}`, 12, 74);

    return canvas.toDataURL('image/png');
  };

  const getLivePreview = () => {
    if (signatureType === 'digital') {
      return renderDigitalSignature(
        smartCaConfig?.fullName || signer?.full_name || 'Người ký',
        smartCaConfig?.issuer || 'VNPT-CA'
      );
    }
    if (signatureType === 'text' && textSignature) {
      return renderTextSignature(textSignature);
    }
    if (signatureType === 'draw' && signatureData?.dataUrl) {
      return signatureData.dataUrl;
    }
    if (signatureType === 'image' && (imagePreview || signatureData?.dataUrl)) {
      return imagePreview || signatureData.dataUrl;
    }
    return null;
  };

  const handleSign = async () => {
    if (!signer || !document) return;

    if (signatureType === 'digital') {
      if (!smartCaConfig?.enabled) {
        setShowSmartCaModal(true);
        return;
      }
    } else if (!hasSignature) {
      setError('Vui lòng tạo chữ ký trước khi ký tài liệu');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      let payload = signatureData;

      if (signatureType === 'text' && textSignature) {
        payload = { ...(signatureData || {}), dataUrl: renderTextSignature(textSignature) };
      } else if (signatureType === 'digital') {
        // Show SmartCA push notification prompt
        setSmartCaPrompting(true);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setSmartCaPrompting(false);

        const digitalStamp = renderDigitalSignature(
          smartCaConfig?.fullName || signer.full_name || 'Người ký',
          smartCaConfig?.issuer || 'VNPT-CA'
        );
        payload = {
          dataUrl: digitalStamp,
          ca_provider: 'VNPT_SMARTCA',
          serial_number: smartCaConfig?.serialNumber,
          subject: smartCaConfig?.fullName,
          username: smartCaConfig?.username,
        };
      }
      await signerApi.sign(signer.id, {
        signature_type: signatureType,
        signature_data: payload,
        signed_at: new Date().toISOString(),
        position: selectedPlacement ? {
          page: selectedPlacement.page,
          x: selectedPlacement.x,
          y: selectedPlacement.y,
          width: selectedPlacement.width,
          height: selectedPlacement.height,
        } : {
          page: stampPage,
          x: 60,
          y: 41.5,
          width: 20,
          height: 6.0,
        },
      });
      setSuccess('Ký tài liệu thành công!');
      await loadSigner();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể ký tài liệu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!signer) return;
    setSubmitting(true);
    setError(null);
    try {
      await signerApi.decline(signer.id, declineReason || undefined);
      setShowDecline(false);
      setSuccess('Đã từ chối ký tài liệu');
      await loadSigner();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể từ chối');
    } finally {
      setSubmitting(false);
    }
  };

  const { user: currentUser } = useAuthStore();

  const handleDownload = () => {
    if (!document || !signer) return;
    const base = import.meta.env.VITE_API_URL || '/api';
    const url = `${base}/signers/${signer.id}/file`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Đang tải tài liệu...</p>
        </div>
      </div>
    );
  }

  if (error && !signer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="card p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Không thể mở tài liệu</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary w-full">
            Về trang chủ
          </button>
        </div>
      </div>
    );
  }

  if (!signer || !document) return null;

  const isSigned = signer.status === 'signed';
  const isDeclined = signer.status === 'declined';
  const isExpired = signer.status === 'expired';
  const isEmailMismatch = Boolean(currentUser && signer && currentUser.email.toLowerCase() !== signer.email.toLowerCase());
  const canSign = !isSigned && !isDeclined && !isExpired && !['completed', 'cancelled', 'rejected', 'expired'].includes(document.status) && !isEmailMismatch;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b0f19] text-slate-900 dark:text-slate-100 transition-colors">
      {/* Top bar */}
      <header className="bg-white dark:bg-[#0e1424] border-b border-slate-200 dark:border-white/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                type="button"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/dashboard');
                  }
                }}
                className="p-2 -ml-1 sm:-ml-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 text-xs font-medium"
                title="Quay lại"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Quay lại</span>
              </button>

              <div className="h-5 w-px bg-slate-200 dark:bg-white/10 hidden sm:block" />

              <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 dark:text-white truncate">{document.title}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{document.file_name} · {document.pages_count} trang</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {currentUser && (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="btn-outline text-xs h-8 px-2.5 sm:px-3 flex items-center gap-1.5"
                  title="Về Dashboard"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Dashboard</span>
                </button>
              )}
              {document.settings?.signing_date && (
                <span className="hidden sm:flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2.5 py-1 rounded-md border border-amber-300 dark:border-amber-600/30">
                  <Clock className="w-3.5 h-3.5" />
                  Ngày ấn định: {document.settings.signing_date}
                </span>
              )}
              {document.expires_at && (
                <span className="hidden sm:flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                  <Clock className="w-4 h-4" />
                  Hạn: {formatDateTime(document.expires_at)}
                </span>
              )}
              <button onClick={handleDownload} className="btn-outline text-xs h-8 px-3" title="Tải PDF">
                <Download className="w-3.5 h-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Tải PDF</span>
              </button>
              <span className={`badge ${
                isSigned ? 'badge-success' :
                isDeclined ? 'badge-danger' :
                isExpired ? 'badge-gray' : 'badge-warning'
              }`}>
                {isSigned ? 'Đã ký' : isDeclined ? 'Đã từ chối' : isExpired ? 'Hết hạn' : 'Chờ ký'}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3 text-danger-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm flex-1">{error}</p>
            <button onClick={() => setError(null)}><XCircle className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center gap-3 text-success-700">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{success}</p>
          </div>
        )}

        {isEmailMismatch && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-600/40 rounded-xl flex items-start gap-3 text-amber-800 dark:text-amber-200 shadow-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
            <div className="text-sm space-y-1">
              <p className="font-semibold">Tài khoản không khớp với người nhận văn bản</p>
              <p>
                Bạn đang đăng nhập bằng tài khoản <strong>{currentUser?.email}</strong>, nhưng lượt ký này được chỉ định riêng cho <strong>{signer.email}</strong>.
              </p>
              <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
                Để đảm bảo tính pháp lý và an toàn dữ liệu, hệ thống không cho phép ký thay hoặc thao tác từ tài khoản khác. Vui lòng đăng xuất hoặc đăng nhập đúng email người nhận.
              </p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* PDF viewer */}
          <div className="lg:col-span-2 space-y-3">
            <InteractivePdfViewer
              documentId={document.id}
              signerId={signer.id}
              initialPage={stampPage}
              signerName={signer.full_name}
              signaturePreview={getLivePreview()}
              onPositionChange={(pos) => {
                setSelectedPlacement(pos);
                setStampPage(pos.page);
                setCurrentPage(pos.page);
              }}
              interactive={canSign}
            />
          </div>

          {/* Signature panel */}
          <div className="space-y-4 lg:sticky lg:top-20">
            {/* Signer info */}
            <div className="card p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Người ký</h3>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-semibold">
                    {signer.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{signer.full_name}</p>
                  <p className="text-sm text-gray-500 truncate">{signer.email}</p>
                </div>
              </div>
              {isSigned && signer.signed_at && (
                <div className="mt-3 p-3.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-500/20 rounded-lg space-y-1.5">
                  <p className="text-sm text-emerald-800 dark:text-emerald-300 flex items-center gap-2 font-medium">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <span>Đã hoàn thành ký văn bản</span>
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">
                    Thời gian ký: <strong>{formatDateTime(signer.signed_at)}</strong>
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 pt-1 border-t border-emerald-200/50 dark:border-emerald-500/10">
                    Chữ ký đã được niêm phong vào tài liệu. Không ai có thể tự ý thay đổi hoặc hủy chữ ký từ liên kết này.
                  </p>
                </div>
              )}
              {isDeclined && (
                <div className="mt-3 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                  <p className="text-sm text-danger-800">
                    Đã từ chối ký{signer.decline_reason ? `: ${signer.decline_reason}` : ''}
                  </p>
                </div>
              )}
            </div>

            {canSign && (
              <>
                {/* Signature type selector */}
                <div className="card p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Chọn cách ký</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {signatureTypes.map((type) => {
                      const Icon = type.icon;
                      const active = signatureType === type.value;
                      return (
                        <button
                          key={type.value}
                          onClick={() => setSignatureType(type.value)}
                          className={`p-3 rounded-lg border-2 text-left transition-colors ${
                            active ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mb-1 ${active ? 'text-primary-600' : 'text-gray-500'}`} />
                          <p className="text-sm font-medium text-gray-900">{type.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Signature input */}
                <div className="card p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {signatureTypes.find(t => t.value === signatureType)?.label}
                  </h3>

                  {signatureType === 'draw' && (
                    <SignaturePad onChange={handleDrawChange} />
                  )}

                  {signatureType === 'image' && (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      {imagePreview ? (
                        <div className="space-y-3">
                          <div className="border border-gray-200 rounded-lg p-3 bg-white flex items-center justify-center min-h-[100px]">
                            <img src={imagePreview} alt="Chữ ký" className="max-h-24 object-contain" />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => fileInputRef.current?.click()} className="btn-outline flex-1 text-sm">
                              Chọn ảnh khác
                            </button>
                            <button
                              onClick={() => { setImagePreview(null); setHasSignature(false); setSignatureData(null); }}
                              className="btn-ghost text-danger-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full border-2 border-dashed rounded-lg p-8 text-center hover:border-primary-400 transition-colors"
                        >
                          <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-gray-900">Chọn ảnh chữ ký</p>
                          <p className="text-xs text-gray-500 mt-1">PNG hoặc JPG, tối đa 5MB</p>
                        </button>
                      )}
                    </div>
                  )}

                  {signatureType === 'text' && (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={textSignature}
                        onChange={(e) => handleTextChange(e.target.value)}
                        placeholder="Nhập tên của bạn..."
                        className="input"
                        maxLength={60}
                      />
                      {textSignature && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-white text-center">
                          <span style={{ fontFamily: 'cursive', fontSize: 28, color: '#1e3a8a' }}>
                            {textSignature}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {signatureType === 'digital' && (
                    <div className="space-y-3">
                      {smartCaConfig?.enabled ? (
                        <div className="p-4 bg-gradient-to-br from-sky-50 to-indigo-50/50 dark:from-sky-950/30 dark:to-indigo-950/20 border border-sky-200 dark:border-sky-500/30 rounded-xl space-y-2.5">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-sky-500 text-white flex items-center justify-center shadow-sm">
                                <ShieldCheck className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-slate-900 dark:text-white">
                                  VNPT SmartCA
                                </p>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                  Chứng thư số từ xa đã kết nối
                                </p>
                              </div>
                            </div>
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30">
                              🟢 Sẵn sàng ký
                            </span>
                          </div>

                          <div className="text-[11px] text-slate-600 dark:text-slate-300 space-y-1 bg-white/70 dark:bg-black/20 p-2.5 rounded-lg border border-slate-100 dark:border-white/5">
                            <p><strong>Người ký:</strong> {smartCaConfig.fullName}</p>
                            <p><strong>Tài khoản:</strong> {smartCaConfig.username}</p>
                            <p><strong>Nhà cấp phát:</strong> {smartCaConfig.issuer}</p>
                            <p><strong>Hiệu lực đến:</strong> {smartCaConfig.validTo}</p>
                          </div>

                          <div className="flex items-center justify-between pt-1">
                            <button
                              type="button"
                              onClick={() => setShowSmartCaModal(true)}
                              className="text-[11px] text-sky-600 dark:text-sky-400 hover:underline"
                            >
                              Đổi tài khoản khác
                            </button>
                            <span className="text-[10px] text-slate-400">
                              Xác thực qua SmartCA App
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-500/20 rounded-xl space-y-3">
                          <div className="flex items-start gap-3">
                            <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-bold text-slate-900 dark:text-white">
                                Chữ ký số VNPT SmartCA
                              </p>
                              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                Ký số chuẩn pháp lý qua ứng dụng VNPT SmartCA trên điện thoại (không cần USB Token).
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowSmartCaModal(true)}
                            className="w-full py-2 px-3 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors flex items-center justify-center gap-1.5"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                            Cấu hình tài khoản VNPT SmartCA ngay
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="card p-5 space-y-2">
                  <button
                    onClick={handleSign}
                    disabled={(!hasSignature && !(signatureType === 'digital' && smartCaConfig?.enabled)) || submitting}
                    className="btn-primary w-full"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Đang ký...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Ký tài liệu</>
                    )}
                  </button>
                  <button
                    onClick={() => setShowDecline(true)}
                    disabled={submitting}
                    className="btn-ghost w-full text-danger-600"
                  >
                    Từ chối ký
                  </button>
                  <p className="text-xs text-gray-500 text-center pt-1">
                    Bằng việc ký, bạn xác nhận đã đọc và đồng ý với nội dung tài liệu.
                  </p>
                </div>
              </>
            )}

            {positions.length > 0 ? (
              <div className="card p-5">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-xs">Vị trí ký</h3>
                <ul className="space-y-1.5">
                  {positions.map((pos) => (
                    <li key={pos.id} className="text-xs text-gray-600 dark:text-slate-300 flex items-center gap-2 font-normal">
                      <span className="badge badge-gray text-[10px]">Trang {pos.page}</span>
                      {pos.label || 'Chữ ký'}
                    </li>
                  ))}
                </ul>
              </div>
            ) : canSign ? (
              <div className="card p-4 space-y-2.5 bg-slate-50/50 dark:bg-slate-900/40">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
                  <h3 className="font-semibold text-xs text-slate-800 dark:text-slate-200">Vị trí đóng dấu chữ ký</h3>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-normal leading-relaxed">
                  Văn bản hành chính mặc định đóng dấu tại góc dưới bên phải <strong>Trang {stampPage} / {document.pages_count}</strong> (khu vực đại diện ký duyệt).
                </p>
                {document.pages_count > 1 && (
                  <div className="pt-2 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-normal">Đổi trang đóng dấu:</span>
                    <select
                      value={stampPage}
                      onChange={(e) => {
                        const p = Number(e.target.value);
                        setStampPage(p);
                        setCurrentPage(p);
                      }}
                      className="select h-7 py-0 px-2 text-xs font-normal"
                    >
                      {Array.from({ length: document.pages_count }, (_, i) => i + 1).map((p) => (
                        <option key={p} value={p}>
                          Trang {p} {p === document.pages_count ? '(Trang cuối)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Decline modal */}
      {showDecline && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowDecline(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Từ chối ký tài liệu</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800">
                <Info className="w-4 h-4 inline mr-1" />
                Tài liệu sẽ chuyển sang trạng thái "Từ chối" và người tạo sẽ được thông báo.
              </div>
              <div>
                <label className="label">Lý do (tùy chọn)</label>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Vui lòng cho biết lý do từ chối..."
                  maxLength={500}
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowDecline(false)} className="btn-outline flex-1" disabled={submitting}>
                  Hủy
                </button>
                <button onClick={handleDecline} className="btn-danger flex-1" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Xác nhận từ chối'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SmartCA Modal */}
      <SmartCaModal
        isOpen={showSmartCaModal}
        onClose={() => setShowSmartCaModal(false)}
      />

      {/* SmartCA Mobile Push Confirmation Dialog */}
      {smartCaPrompting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-white/10 rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center space-y-4">
            <div className="relative inline-flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-sky-500/20 animate-ping"></div>
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-tr from-sky-500 to-indigo-600 flex items-center justify-center text-white shadow-xl shadow-sky-500/30">
                <Smartphone className="w-8 h-8" />
              </div>
            </div>

            <div className="space-y-1">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                Vui lòng xác nhận trên điện thoại
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                Đã gửi yêu cầu ký số đến ứng dụng <strong>VNPT SmartCA</strong> của bạn.
              </p>
            </div>

            <div className="p-3 bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-500/20 rounded-xl text-xs text-sky-800 dark:text-sky-300 flex items-center gap-2 text-left">
              <Loader2 className="w-4 h-4 animate-spin text-sky-500 flex-shrink-0" />
              <span>Đang chờ bạn quét FaceID hoặc nhập mã PIN trên ứng dụng điện thoại...</span>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={() => setSmartCaPrompting(false)}
                className="btn-ghost text-xs w-full"
              >
                Hủy yêu cầu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}